import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { featureIdForPath } from "../config/pricingCatalog";

/**
 * Records the feature the user is currently looking at, so that when they later
 * hit the global "Pricing" nav link (which carries no context of its own), the
 * pricing page can still lead with the right feature's outcomes.
 *
 * Call this once near the top of each feature page / feature landing page:
 *   useFeatureContext("tech-pack");
 *
 * The pricing page reads ?from=<id> first and falls back to this value.
 */
export const FEATURE_CONTEXT_KEY = "adstro:lastFeature";

export function useFeatureContext(featureId) {
  useEffect(() => {
    if (!featureId) return;
    try {
      sessionStorage.setItem(FEATURE_CONTEXT_KEY, featureId);
    } catch (_) {
      /* sessionStorage unavailable (private mode / SSR) — non-fatal */
    }
  }, [featureId]);
}

export function getStoredFeatureContext() {
  try {
    return sessionStorage.getItem(FEATURE_CONTEXT_KEY);
  } catch (_) {
    return null;
  }
}

/**
 * Mount once inside the Router. Records the feature whenever the user is on any
 * feature route or feature landing route, so the global "Pricing" nav link
 * (which carries no ?from=) still lands them on the right contextual view.
 * Renders nothing.
 */
export function FeatureContextTracker() {
  const location = useLocation();
  useEffect(() => {
    const id = featureIdForPath(location.pathname);
    if (!id) return;
    try {
      sessionStorage.setItem(FEATURE_CONTEXT_KEY, id);
    } catch (_) {
      /* non-fatal */
    }
  }, [location.pathname]);
  return null;
}
